import { Field, RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { RichTextProps } from '@sitecore-jss/sitecore-jss-nextjs/types/components/RichText';
import { useJoinCode } from 'components/globalContext/JoinCodeContext';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { isDownloadLink, isExternalLink, withGtmLinkHelper } from 'lib/util/gtm-datalayer';
import { setRelNoReffer } from 'lib/util/gtm-datalayer/_helpers';
import { handleJoinCode } from 'lib/util/join-code-helper';
import React, { useEffect, useState } from 'react';

const srOnlySpan = '<span class="sr-only"> (Opens in a new tab)</span>';
const newTabIcon =
  '<span class="svg-icon inline-flex align-middle h-em ml-0.5"><svg version="1.1" xmlns="http://www.w3.org/2000/svg" ' +
  'xmlns:xlink="http://www.w3.org/1999/xlink" x="0" y="0" width="24" height="24" viewBox="0, 0, 10, 24"><path xmlns="http://www.w3.org/2000/svg" ' +
  'class="st0" d="M14.5,0h-4.29C9.76,0,9.54,0.54,9.85,0.85l1.44,1.44l-5,5c-0.39,0.39-0.39,1.02,0,1.41 ' +
  ' c0.39,0.39,1.03,0.39,1.42,0l5-5l1.44,1.44C14.46,5.46,15,5.24,15,4.79V0.5C15,0.22,14.78,0,14.5,0z M2,1C0.9,1,0,1.9,0,3v10 ' +
  ' c0,1.1,0.9,2,2,2h10c1.1,0,2-0.9,2-2V9.1c0-0.55-0.45-1-1-1s-1,0.45-1,1V13H2V3h4.4c0.55,0,1-0.45,1-1s-0.45-1-1-1H2z" fill="currentColor"/></svg></span>';

const RichTextA11yWrapper = ({ field, editable = true, ...props }: RichTextProps): JSX.Element => {
  const joinCode = useJoinCode();
  /**
   * Hook for experience editor
   */
  const isExperienceEditor = useExperienceEditor();

  const [content, setContent] = useState(field?.value);

  const updatedField: Field<string> = {
    value: content?.length ? content : '',
  };

  // Run this client-side because we don't have access to the document server-side
  useEffect(() => {
    if (isExperienceEditor) {
      return;
    }
    // Install client-side link helper
    const theWindow = withGtmLinkHelper(window);

    const template = document.createElement('template');
    template.innerHTML = field?.value?.length ? field.value : '';

    // Scan through all hyperlinks because they should be magic
    [...template.content.querySelectorAll('a')].forEach((a) => {
      // Attach to native onclick for GTM datalayer analytics.  Note we should probably get fancy here and preserve any pre-existing onclick
      if (theWindow) {
        a.setAttribute('onclick', 'return gtmClickHelper.linkClicked(event)');
      }
      // Set rel attribute for external links only
      if (isExternalLink(a) && !isDownloadLink(a)) {
        setRelNoReffer(a);
      }
      // Find all links either either have target="_blank" or appear to be external due to starting with "http"
      // Update: Additonal implementation for not showing arrow for internal links
      if (isExternalLink(a) || isDownloadLink(a)) {
        // Set to open in new tab
        a.setAttribute('target', '_blank');
        // Add Screen Reader text and new tab icon
        // eslint-disable-next-line no-param-reassign
        a.innerHTML = `${a.innerHTML}${srOnlySpan}${newTabIcon}`;
      }
      handleJoinCode({ a, joinCode });
    });

    // Update the content
    setContent(template.innerHTML);
  }, [field?.value, joinCode, isExperienceEditor]);

  // If there's no text, the only render it if it is editable and we are editing.
  // Otherwise don't render anything
  if (!field?.value) {
    if (isExperienceEditor && editable) {
      return <RichText field={field} {...props} editable={editable} />;
    }
    return <></>;
  }

  // Just a pass through if it's EE.
  if (isExperienceEditor) {
    return <RichText field={field} {...props} editable={editable} />;
  }
  /**
   * If not EE, use the updated field value
   */
  return <RichText field={updatedField} {...props} editable={editable} />;
};

export default RichTextA11yWrapper;
